import Popover from 'primevue/popover';
import Select from 'primevue/select';

interface OverlayInstance {
    resizeListener: (() => void) | null
    alignOverlay: () => void
    // Popover
    visible?: boolean
    container?: HTMLElement | null
    target?: HTMLElement | null
    // Select
    overlayVisible?: boolean
    overlay?: HTMLElement | null
    $el?: HTMLElement | null
}

interface OverlayState {
    open?: boolean
    overlay?: HTMLElement | null
    target?: HTMLElement | null
}

// PrimeVue positions an anchored overlay once, when it opens, and on resize only hides it - and even
// that only when the device is not touch-capable. navigator.maxTouchPoints > 0 on most Windows
// laptops, so there the panel neither hid nor moved and stayed pinned to the coordinates it got
// before the resize (open the picker in a small window, maximise, the calendar hangs far from its
// trigger). Hiding is not what it should do either, so realign against the trigger on every resize,
// touch and non-touch alike.
function realignOnResize(
    component: unknown,
    readState: (instance: OverlayInstance) => OverlayState,
) {
    const patched = component as { methods: Record<string, unknown> };

    patched.methods.bindResizeListener = (
        function bindResizeListener(this: OverlayInstance) {
            if (this.resizeListener) {
                return;
            }

            this.resizeListener = () => {
                const { open, overlay, target } = readState(this);

                if (!open || !overlay || !target?.isConnected) {
                    return;
                }

                // alignOverlay only ever sets the flipped marker, never clears it, so a panel that
                // flipped above its trigger in the small viewport would keep the flipped arrow
                // after the resize moves it back below.
                overlay.removeAttribute('data-p-popover-flipped');
                overlay.classList.remove('p-popover-flipped');

                this.alignOverlay();
            };

            window.addEventListener('resize', this.resizeListener);
        }
    );
}

realignOnResize(Popover, (instance) => ({
    open: instance.visible,
    overlay: instance.container,
    target: instance.target,
}));

/* Set on a popover's root through pt to opt into the two placement rules below. */
export const ALIGN_END_CLASS = 'popover-align-end';
export const NO_FLIP_CLASS = 'popover-no-flip';

const basePopoverAlign = (Popover as unknown as {
    methods: { alignOverlay: (this: OverlayInstance) => void }
}).methods.alignOverlay;

(Popover as unknown as { methods: Record<string, unknown> }).methods.alignOverlay = (
    function alignOverlay(this: OverlayInstance) {
        basePopoverAlign.call(this);

        const { container, target } = this;

        if (!container || !target) {
            return;
        }

        const targetRect = target.getBoundingClientRect();

        if (container.classList.contains(ALIGN_END_CLASS)) {
            const insetStart = targetRect.right + window.scrollX - container.offsetWidth;
            container.style.insetInlineStart = `${Math.max(0, insetStart)}px`;
        }

        if (container.classList.contains(NO_FLIP_CLASS)) {
            container.style.top = `${targetRect.bottom + window.scrollY}px`;
            container.style.transformOrigin = 'top';
            container.removeAttribute('data-p-popover-flipped');
            container.classList.remove('p-popover-flipped');
        }
    }
);

// Covers every Select in the portal, the year dropdown inside the date filter included.
realignOnResize(Select, (instance) => ({
    open: instance.overlayVisible,
    overlay: instance.overlay,
    target: instance.$el,
}));
